import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import EditIcon from "@mui/icons-material/Edit";

import type { SignOffField } from "../../shared/api-types/training";

import Button from "../../components/Button";
import { updateSignOffDate } from "../../api/training";
import { captureHandledException } from "../../services/sentry";
import { getTranslatedApiError } from "../../utils/apiError";

import SignOffDateDialog from "./SignOffDateDialog";
import { SIGN_OFF_FIELD_LABEL_KEY } from "./signOffRoles";
import { useCanEditSignOffDate } from "./useCanEditSignOffDate";

export interface SignOffDateButtonProps {
  /** `user_2_training.id` of the row being corrected. */
  trainingUserId: number;
  field: SignOffField;
  /** Currently stored sign-off date, or null if the API returned none. */
  date: string | null;
  /** Called after a successful save so the screen can refetch. */
  onSaved: () => void;
}

/**
 * Pencil next to a granted sign-off, opening a dialog to correct the date it was completed.
 * Sign-offs are routinely recorded days after the fact, and the stored date drives the
 * certificate's expiry — so it has to be correctable after the switch has been flipped.
 */
const SignOffDateButton: FC<SignOffDateButtonProps> = ({ trainingUserId, field, date, onSaved }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canEdit = useCanEditSignOffDate(field);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = () => {
    setError(null);
    setOpen(true);
  };

  const handleSave = async (newDate: string) => {
    setSaving(true);
    setError(null);
    try {
      await updateSignOffDate(trainingUserId, field, newDate);
      enqueueSnackbar(t("training_pages.sign_off_date_updated"), { variant: "success" });
      setOpen(false);
      onSaved();
    } catch (err) {
      captureHandledException(err, {
        feature: "training-sign-off-date",
        component: "SignOffDateButton",
        action: "updateSignOffDate",
        trainingUserId,
        field,
      });
      setError(await getTranslatedApiError(err, t, "training_pages.sign_off_date_update_failed"));
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) return null;

  return (
    <>
      <Button title={t("training_pages.edit_sign_off_date")} icon={<EditIcon />} onClick={handleOpen} />
      <SignOffDateDialog
        open={open}
        date={date}
        fieldLabel={t(SIGN_OFF_FIELD_LABEL_KEY[field])}
        saving={saving}
        error={error}
        onClose={() => setOpen(false)}
        onSave={handleSave}
      />
    </>
  );
};

export default SignOffDateButton;
